/**
 * ตัวคุยกับ Print Bridge ที่รันอยู่บนเครื่อง Sunmi เครื่องเดียวกัน
 *
 * ★ หลักการเดียวที่สำคัญที่สุดของไฟล์นี้ ★
 * การพิมพ์ล้มเหลว "ห้าม" ทำให้ปิดบิลไม่ได้
 *
 * กระดาษหมด เครื่องพิมพ์ค้าง บลูทูธหลุด — เกิดขึ้นกลางวันขายจริงทั้งนั้น
 * ถ้าปิดบิลไม่ได้เพราะพิมพ์ไม่ออก คิวจะยาวทันที ฟังก์ชันทุกตัวที่นี่จึงไม่ throw
 * แต่คืนผลว่าสำเร็จหรือไม่ ให้หน้าจอเลือกเองว่าจะเตือนแบบไหน
 *
 * ที่อยู่เป็น http://localhost — เบราว์เซอร์ยอมให้หน้า HTTPS เรียกได้
 * พิสูจน์แล้วบนเครื่องจริง (ดู apps/print-bridge/bridge-test.html)
 */

import { CMD_DRAWER_KICK, canvasToPrintBytes, toBase64 } from './escpos.ts';

const PORT_KEY = 'sushimai.bridgePort';
const DEFAULT_PORT = 9110;

export function bridgePort(): number {
  const stored = Number(localStorage.getItem(PORT_KEY));
  return Number.isInteger(stored) && stored > 0 ? stored : DEFAULT_PORT;
}

export function setBridgePort(port: number): void {
  localStorage.setItem(PORT_KEY, String(port));
}

const base = () => `http://localhost:${bridgePort()}`;

export interface BridgeResult {
  ok: boolean;
  /** ข้อความอธิบายเมื่อไม่สำเร็จ — เขียนให้พนักงานรู้ว่าต้องทำอะไรต่อ */
  message?: string;
}

/** งานพิมพ์ที่ยิงไม่สำเร็จ เก็บไบต์เดิมไว้ให้กดพิมพ์ซ้ำได้จากหน้าจอ */
export interface FailedJob {
  id: number;
  label: string;
  kind: PrintKind;
  escposBase64: string;
  printerName: string;
  at: number;
}

export type PrintKind = 'bill' | 'receipt' | 'kitchen' | 'reprint';

const failed: FailedJob[] = [];
let nextId = 1;

export function failedJobs(): readonly FailedJob[] {
  return failed;
}

export function clearFailedJob(id: number): void {
  const i = failed.findIndex((j) => j.id === id);
  if (i >= 0) failed.splice(i, 1);
}

function explain(err: unknown): string {
  const message = err instanceof Error ? err.message : String(err);
  if (/Failed to fetch|NetworkError|Load failed/i.test(message)) {
    return 'ต่อกับตัวพิมพ์ในเครื่องไม่ได้ — เปิดแอป Print Bridge ค้างไว้แล้วลองใหม่';
  }
  return message;
}

async function call(path: string, body?: unknown): Promise<BridgeResult> {
  try {
    const res = await fetch(base() + path, {
      method: body === undefined ? 'GET' : 'POST',
      headers: body === undefined ? undefined : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      cache: 'no-store',
    });
    // ★ อ่านเนื้อคำตอบเสมอ แม้รหัสจะไม่ใช่ 200 ★
    // ตัวพิมพ์ตอบ 500 พร้อมเหตุผลเป็นภาษาคนมาด้วย เช่น "บลูทูธปิดอยู่"
    // ถ้าดูแต่รหัสจะเหลือแค่ "รหัส 500" ซึ่งไม่ช่วยให้พนักงานแก้อะไรได้เลย
    const data = (await res.json().catch(() => null)) as
      | { ok?: boolean; message?: string; error?: string }
      | null;
    const said = data?.message ?? data?.error;

    if (!res.ok || data?.ok === false) {
      return { ok: false, message: said ?? `ตัวพิมพ์ตอบรหัส ${res.status}` };
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, message: explain(err) };
  }
}

export function ping(): Promise<BridgeResult> {
  return call('/ping');
}

/**
 * ส่งสลิปไปพิมพ์
 *
 * ★ แปลงเป็นคำสั่ง ESC/POS ที่นี่ แล้วส่งไบต์ดิบไปให้ตัวพิมพ์ ★
 *
 * เรนเดอร์เป็นภาพก่อนเสมอ ไม่ส่งเป็นข้อความ — ตัดปัญหา codepage ภาษาไทย
 * ที่เครื่องพิมพ์แต่ละยี่ห้อรองรับไม่เหมือนกันจนสระลอยผิดตำแหน่ง
 *
 * แต่การอัดภาพเป็นบิตทำที่นี่ ไม่ใช่บนเครื่อง Android เพราะฝั่งนี้ทดสอบได้ทุกไบต์
 * (ดู lib/escpos.ts) ตัวพิมพ์บนเครื่องจึงเหลือหน้าที่เดียวคือเขียนไบต์ลงบลูทูธ
 *
 * @param printerName ชื่ออุปกรณ์บลูทูธที่จับคู่ไว้ เช่น InnerPrinter
 */
export async function printImage(
  kind: PrintKind,
  canvas: HTMLCanvasElement,
  options: { label: string; printerName?: string } = { label: '' },
): Promise<BridgeResult> {
  let escposBase64: string;
  try {
    escposBase64 = toBase64(canvasToPrintBytes(canvas));
  } catch (err) {
    return { ok: false, message: explain(err) };
  }

  const result = await call('/print', {
    kind,
    printerName: options.printerName ?? '',
    escposBase64,
  });

  if (!result.ok) {
    // ★ เก็บไบต์เดิมไว้ ★ พิมพ์ซ้ำแล้วได้สลิปเหมือนเดิมเป๊ะ ไม่ต้องเรนเดอร์ใหม่
    failed.push({
      id: nextId++, label: options.label, kind,
      escposBase64, printerName: options.printerName ?? '', at: Date.now(),
    });
  }
  return result;
}

/** พิมพ์ซ้ำงานที่เคยล้มเหลว — ส่งไบต์ชุดเดิม */
export async function reprintFailed(job: FailedJob): Promise<BridgeResult> {
  const result = await call('/print', {
    kind: job.kind,
    printerName: job.printerName,
    escposBase64: job.escposBase64,
  });
  if (result.ok) clearFailedJob(job.id);
  return result;
}

/**
 * เตะลิ้นชักให้เปิด
 *
 * ★ เรียกเฉพาะเมื่อ API ตอบว่า shouldOpenDrawer เป็นจริงเท่านั้น ★
 * ระบบเดิมเปิดทุกครั้งที่ปิดบิล ทั้งที่เกือบ 4 ใน 5 บิลไม่มีเงินสดเลย
 *
 * ลิ้นชักต่ออยู่กับพอร์ต 24V ที่ตัวเครื่องพิมพ์ จึงสั่งผ่านเครื่องพิมพ์ทางเดียว
 */
export function kickDrawer(reason: string, printerName = ''): Promise<BridgeResult> {
  return call('/drawer', {
    reason,
    printerName,
    escposBase64: toBase64(CMD_DRAWER_KICK),
  });
}
